import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { UndoStateModel, UndoStack } from './undo.state.model';
import { UndoActions } from './undo.state.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { StoryChaptersStateModel } from 'src/app/story/state/chapters/story.chapters.state.model';
import { get } from 'lodash';
import { Injectable } from '@angular/core';

declare const immer;

const initialState: UndoStateModel = {
  stacksForChapter: {}
};

@State<UndoStateModel>({
  name: 'undo',
  defaults: initialState
})
@Injectable()
export class UndoState {
  constructor(private store: Store) {}

  @Selector([StoryChaptersState])
  static canUndo(
    state: UndoStateModel,
    chapterState: StoryChaptersStateModel
  ): boolean {
    return (
      state.stacksForChapter[chapterState.selectedChapter].undoStack.length > 0
    );
  }

  @Selector([StoryChaptersState])
  static canRedo(
    state: UndoStateModel,
    chapterState: StoryChaptersStateModel
  ): boolean {
    return (
      state.stacksForChapter[chapterState.selectedChapter].redoStack.length > 0
    );
  }

  @Selector([StoryChaptersState])
  static getUndoCount(
    state: UndoStateModel,
    chapterState: StoryChaptersStateModel
  ): number {
    return (
      state.stacksForChapter[chapterState.selectedChapter].undoStack.length || 0
    );
  }

  @Action(UndoActions.Reset) 
  reset(context: StateContext<UndoStateModel>, action: UndoActions.Reset) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        draft.stacksForChapter = {};
      })
    );
  }

  @Action(UndoActions.UndoAll)
  undoAll(context: StateContext<UndoStateModel>, action: UndoActions.UndoAll) {

    const state = context.getState();

    if (state.stacksForChapter[action.chapterId] === undefined) {
      return;
    }

    const patches = [
      ...state.stacksForChapter[action.chapterId].undoStack
    ].reverse();

    // batch all patches of the same kind to be dispatched at once.
    const sortedPatches = patches.flat().reduce((sorted, patch) => {
      if (sorted[patch.patchAction] === undefined) {
        sorted[patch.patchAction] = {
          action: patch.patchAction,
          patches: []
        };
      }

      sorted[patch.patchAction].patches = [
        ...sorted[patch.patchAction].patches,
        ...patch.inverse
      ];
      return sorted;
    }, {});

    Object.keys(sortedPatches).forEach(actionName => {
      context.dispatch(
        new sortedPatches[actionName].action(sortedPatches[actionName].patches)
      );
    });



    const nextState = immer.produce(state, draft => {
      draft.stacksForChapter[action.chapterId].undoStack = [];
      draft.stacksForChapter[action.chapterId].redoStack = patches;
    });

    context.patchState(nextState);
  }

  @Action(UndoActions.SavePatches)
  preserveState(
    context: StateContext<UndoStateModel>,
    action: UndoActions.SavePatches
  ) {
    const chapterId = this.store.selectSnapshot(
      StoryChaptersState.getSelectedChapterId
    );

    const nextState = immer.produce(context.getState(), draft => {

      if (action.patches.length === 0) {
        return;
      }

      if (
        action.patches.some((patch) => patch.forward.length === 0) &&
        action.patches.some((patch) => patch.inverse.length === 0)
      ) {
        return;
      }
      
      if (draft.stacksForChapter[chapterId] === undefined) {
        draft.stacksForChapter[chapterId] = { redoStack: [], undoStack: [] };
      }

      draft.stacksForChapter[chapterId].redoStack = [];
      const undoStack = draft.stacksForChapter[chapterId].undoStack as UndoStack;
      undoStack.push(action.patches);
    });

    context.patchState(nextState);
  }

  @Action(UndoActions.Redo)
  redo(context: StateContext<UndoStateModel>, action: UndoActions.Redo) {

    const state = context.getState();
    const redoStack = get(state.stacksForChapter[action.chapterId], 'redoStack', []) as UndoStack;

    if (redoStack.length === 0) {
      return;
    }

    const savePatches = redoStack[redoStack.length - 1];
    const nextState = immer.produce(state, draft => {
      draft.stacksForChapter[action.chapterId].redoStack.pop();
      draft.stacksForChapter[action.chapterId].undoStack.push(savePatches);
    });

    context.patchState(nextState);
    savePatches.forEach(patch => {
      context.dispatch(new patch.patchAction(patch.forward));
    });
  }

  @Action(UndoActions.Undo)
  undo(context: StateContext<UndoStateModel>, action: UndoActions.Undo) {

    const state = context.getState();
    const undoStack = get(state.stacksForChapter[action.chapterId], 'undoStack', []) as UndoStack;

    if (undoStack.length === 0) {
      return;
    }

    const savePatches = undoStack[undoStack.length - 1];
    const nextState = immer.produce(state, draft => {
      draft.stacksForChapter[action.chapterId].undoStack.pop();
      draft.stacksForChapter[action.chapterId].redoStack.push(savePatches);
    });

    context.patchState(nextState);
    savePatches.forEach(patch => {
      context.dispatch(new patch.patchAction(patch.inverse));
    });
  }
}
